import React, { useState, useEffect, useRef } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useTheme } from "../../contexts/ThemeContext";
import { getApiUrl } from "../../config/api";
import { elitePulseFetch } from "../../utils/elitePulseFetch";
import { useAuth } from "../../contexts/AuthContext";
import { useWatchlist } from "../../contexts/WatchlistContext";
import { encryptError } from "../../utils/aiErrorCodes";
import { forceSessionExpired, getAuthToken } from "../../utils/authUtils";
import WatchlistManager from "./WatchlistManager";
import { useTradingCall } from "../../contexts/TradingCallContext";

/** Chat mode: general (data + report), option_helper (contract/strategy help), strategy_trading_calls (create/track calls, train model). */
export type ChatMode = "general" | "option_helper" | "strategy_trading_calls";
import {
    MMEC_PRESET_MARKET_VIEW,
    MMEC_PRESET_STOCK_ANALYSIS,
    MMEC_PRESET_STRATEGY,
    MMEC_PRESET_ALERTS,
    getPresetQuestionText,
} from "./mmecPresetQuestions";
import {
    loadFlowAlertSettings,
    summarizeFlowAlertSettings,
} from "../../utils/flowAlertSettingsStore";
import {
    formatAlertSettingsForChat,
    isShowAlertSettingsQuery,
} from "../../utils/alertSettingsChat";
import { fetchUserAlertProfile } from "../../services/userAlertProfileService";
import { extractAlertConfigFromMessage } from "../../utils/aiAlertConfigParser";
import {
    fetchAlertCatalog,
    persistAlertPatch,
    syncAlertProfileFromServer,
    type AlertPreviewResult,
} from "../../services/userAlertProfileService";
import {
    ELITE_PULSE_NEW_REPORT_EVENT,
    type ElitePulseReportNotifyPayload,
} from "../../utils/elitePulseReportNotify";
import { buildChatPostMarketContext } from "../../utils/aiChatMarketContext";

/** Clean AI response for display: less markdown noise; never expose internal agent names. */
function sanitizeAiMessage(content: string): string {
    if (!content || typeof content !== "string") return content;
    let s = content
        .replace(/\*\*/g, "")
        .replace(/^#{1,6}\s*/gm, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .replace(/⚡\s*Fast local[^\n]*\n?/gi, "")
        .replace(/⚡\s*رد سريع[^\n]*\n?/g, "")
        .replace(/^(Ollama|DeepSeek|MMEC-A\d+|MMEC-OLLAMA)[^\n]*\n?/gim, "")
        .replace(/\b(Ollama|DeepSeek)\b/gi, "")
        .replace(/learning in parallel with DeepSeek/gi, "")
        .replace(/يتعلم بالتوازي مع DeepSeek/g, "")
        .trim();
    return s;
}

interface Message {
    id: string;
    type: "user" | "ai" | "system";
    content: string;
    timestamp: Date;
    language?: string;
    provider?: string;
}

/** AI Chat element: floating chat panel (bottom-right) for options/market and Elite Pulse report Q&A. */
interface AIChatWidgetProps {
    isCollapsed?: boolean;
    onToggle?: () => void;
    marketContext?: any;
    /** When true, fills parent instead of fixed bottom-right (companion shell). */
    embedded?: boolean;
}

const AIChatWidget: React.FC<AIChatWidgetProps> = ({
    isCollapsed = false,
    onToggle,
    marketContext = {},
    embedded = false,
}) => {
    const { language } = useLanguage();
    const { colors } = useTheme();
    const [internalCollapsed, setInternalCollapsed] = useState(false);
    const effectiveCollapsed =
        onToggle !== undefined ? isCollapsed : internalCollapsed;
    const handleToggle = () => {
        if (onToggle) onToggle();
        else setInternalCollapsed((c) => !c);
    };
    const { token } = useAuth();
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputMessage, setInputMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedProvider, setSelectedProvider] = useState<string>("auto");
    const [, setAvailableProviders] = useState<any>({});
    const [providerHealthOk, setProviderHealthOk] = useState<boolean | null>(
        null,
    );
    const [showWatchlist, setShowWatchlist] = useState<boolean>(false);
    const [discussOnlyReport, setDiscussOnlyReport] = useState<boolean>(false);
    const [presetSectionOpen, setPresetSectionOpen] = useState<boolean>(false);
    const [presetSymbol, setPresetSymbol] = useState<string>("");
    const [chatMode, setChatMode] = useState<ChatMode>("general");
    const [showStrategyTable, setShowStrategyTable] = useState(false);
    const [strategyRecommendations, setStrategyRecommendations] = useState<
        any[]
    >([]);
    const [trackModalOpen, setTrackModalOpen] = useState(false);
    const [trackPayload, setTrackPayload] = useState<{
        symbol: string;
        strategy_type: string;
        snippet: string;
    }>({ symbol: "", strategy_type: "", snippet: "" });
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const { watchlist } = useWatchlist();
    const { activeCalls, closedCalls } = useTradingCall();

    const isCompanionEmbed =
        embedded &&
        Boolean(
            (marketContext as { companion_mode?: boolean })?.companion_mode,
        );

    // Scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const loadProviders = async () => {
        try {
            const response = await fetch(getApiUrl("/api/ai/providers"), {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                setProviderHealthOk(false);
                return;
            }
            const text = await response.text();
            if (!text || !text.trim()) {
                setProviderHealthOk(false);
                return;
            }
            let data: {
                success?: boolean;
                providers?: {
                    primary?: string;
                    default_chat_provider?: string | null;
                    deepseek?: { available?: boolean };
                    glm?: { available?: boolean };
                    ollama?: { ready?: boolean; available?: boolean };
                };
            };
            try {
                data = JSON.parse(text);
            } catch {
                setProviderHealthOk(false);
                return;
            }
            if (data?.success && data.providers) {
                setAvailableProviders(data.providers);
                const dsOk = Boolean(data.providers.deepseek?.available);
                const glmOk = Boolean(data.providers.glm?.available);
                const olOk = Boolean(
                    data.providers.ollama?.ready ??
                        data.providers.ollama?.available,
                );
                setProviderHealthOk(dsOk || glmOk || olOk);
                const def = data.providers.default_chat_provider;
                if (def === "deepseek" || def === "glm" || def === "ollama") {
                    setSelectedProvider(def);
                } else if (selectedProvider !== "auto" && !dsOk && !glmOk && !olOk) {
                    setSelectedProvider("auto");
                }
            } else {
                setProviderHealthOk(false);
            }
        } catch {
            setProviderHealthOk(false);
        }
    };

    // Load / refresh provider health (on mount, when token arrives, every 60s)
    useEffect(() => {
        void loadProviders();
        const id = window.setInterval(() => void loadProviders(), 60_000);
        return () => window.clearInterval(id);
    }, [token]);
    useEffect(() => {
        const onNewPulseReport = (ev: Event) => {
            const detail = (
                ev as CustomEvent<
                    ElitePulseReportNotifyPayload & {
                        openChat?: boolean;
                        discussReport?: boolean;
                        navigate?: boolean;
                    }
                >
            ).detail;
            if (!detail) return;
            if (detail.openChat || detail.discussReport) {
                if (onToggle && effectiveCollapsed) onToggle();
                else setInternalCollapsed(false);
            }
            if (detail.discussReport) {
                setDiscussOnlyReport(true);
            }
            if (detail.openChat || detail.discussReport) {
                const ar = language === "ar";
                const when = detail.generatedAt
                    ? new Date(detail.generatedAt).toLocaleString(
                          ar ? "ar" : "en-US",
                          {
                              dateStyle: "short",
                              timeStyle: "short",
                          },
                      )
                    : "";
                const intro = ar
                    ? `تقرير نبض النخبة الجديد (${when}). أبرز الفرص:\n${detail.previewText}\n\nاسأل عن أي رمز أو فرصة في هذا التقرير.`
                    : `New Elite Pulse report (${when}). Top opportunities:\n${detail.previewText}\n\nAsk about any symbol or setup in this report.`;
                setMessages((prev) => [
                    ...prev,
                    {
                        id: `pulse-${Date.now()}`,
                        type: "system",
                        content: intro,
                        timestamp: new Date(),
                    },
                ]);
            }
        };
        window.addEventListener(ELITE_PULSE_NEW_REPORT_EVENT, onNewPulseReport);
        return () =>
            window.removeEventListener(
                ELITE_PULSE_NEW_REPORT_EVENT,
                onNewPulseReport,
            );
    }, [effectiveCollapsed, language, onToggle]);

    // Sync per-user alert profile + field catalog from server
    useEffect(() => {
        if (!token) return;
        void syncAlertProfileFromServer(token);
        void fetchAlertCatalog(token);
    }, [token]);

    // Load strategy recommendations table when opened
    const fetchStrategyRecommendations = async () => {
        try {
            const res = await fetch(
                getApiUrl("/api/ai/strategy-recommendations"),
                {
                    headers: { Authorization: `Bearer ${token}` },
                },
            );
            const data = await res.json();
            if (data.success && Array.isArray(data.recommendations)) {
                setStrategyRecommendations(data.recommendations);
            }
        } catch (e) {
            console.warn("Strategy recommendations fetch failed", e);
        }
    };
    useEffect(() => {
        if (showStrategyTable) fetchStrategyRecommendations();
    }, [showStrategyTable, token]);

    const handleTrackRecommendation = (snippet: string) => {
        setTrackPayload({
            symbol: presetSymbol || "",
            strategy_type: "",
            snippet: snippet.slice(0, 1500),
        });
        setTrackModalOpen(true);
    };

    const submitTrackRecommendation = async () => {
        if (!trackPayload.symbol.trim() || !trackPayload.strategy_type.trim())
            return;
        try {
            const res = await fetch(
                getApiUrl("/api/ai/strategy-recommendations"),
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        symbol: trackPayload.symbol.trim(),
                        strategy_type: trackPayload.strategy_type.trim(),
                        raw_message_snippet: trackPayload.snippet,
                    }),
                },
            );
            const data = await res.json();
            if (data.success) {
                setTrackModalOpen(false);
                setTrackPayload({ symbol: "", strategy_type: "", snippet: "" });
                if (showStrategyTable) fetchStrategyRecommendations();
            }
        } catch (e) {
            console.warn("Track recommendation failed", e);
        }
    };

    const recordOutcome = async (
        recId: string,
        outcome: string,
        pnl?: number,
    ) => {
        try {
            await fetch(
                getApiUrl(`/api/ai/strategy-recommendations/${recId}/outcome`),
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ outcome, pnl }),
                },
            );
            if (showStrategyTable) fetchStrategyRecommendations();
        } catch (e) {
            console.warn("Record outcome failed", e);
        }
    };

    const replyWithAlertSettings = async (userText: string) => {
        const userMessage: Message = {
            id: Date.now().toString(),
            type: "user",
            content: userText,
            timestamp: new Date(),
        };
        setMessages((prev) => [...prev, userMessage]);
        setIsLoading(true);
        setError(null);
        try {
            if (token) await syncAlertProfileFromServer(token);
            let settings = loadFlowAlertSettings();
            if (token) {
                const remote = await fetchUserAlertProfile(token);
                if (remote) settings = { ...settings, ...remote };
            }
            const lang = language === "ar" ? "ar" : "en";
            const body = formatAlertSettingsForChat(settings, lang);
            const aiMessage: Message = {
                id: (Date.now() + 1).toString(),
                type: "ai",
                content: body,
                timestamp: new Date(),
                provider: "alert_settings",
            };
            setMessages((prev) => [...prev, aiMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const sendMessage = async () => {
        if (!inputMessage.trim() || isLoading) return;

        const currentInput = inputMessage.trim();
        if (isShowAlertSettingsQuery(currentInput)) {
            setInputMessage("");
            await replyWithAlertSettings(currentInput);
            return;
        }

        const userMessage: Message = {
            id: Date.now().toString(),
            type: "user",
            content: inputMessage,
            timestamp: new Date(),
        };

        // Add user message to state first
        const updatedMessages = [...messages, userMessage];
        setMessages(updatedMessages);
        setInputMessage("");
        setIsLoading(true);
        setError(null);

        try {
            // Build conversation history from updated messages (includes current user message)
            const conversationHistory = updatedMessages
                .filter((m) => m.type === "user" || m.type === "ai")
                .slice(-10)
                .map((m) => ({
                    role: m.type === "user" ? "user" : "assistant",
                    content: m.content,
                }));

            const provider =
                selectedProvider === "auto" ? undefined : selectedProvider;

            // When "Discuss only the report" is on, fetch Elite Pulse report and send as context
            let enrichedMarketContext = { ...marketContext };
            if (isCompanionEmbed) {
                enrichedMarketContext = {
                    ...enrichedMarketContext,
                    companion_mode: true,
                    chat_mode: chatMode,
                };
                const parentCtx = (
                    marketContext as {
                        companion_context?: Record<string, unknown>;
                    }
                )?.companion_context;
                if (parentCtx && typeof parentCtx === "object") {
                    enrichedMarketContext.companion_context = parentCtx;
                }
                try {
                    const headers: Record<string, string> = {
                        Accept: "application/json",
                    };
                    if (token) headers.Authorization = `Bearer ${token}`;
                    const cRes = await fetch(
                        getApiUrl("/api/companion/state"),
                        { headers },
                    );
                    if (cRes.ok) {
                        const cData = await cRes.json();
                        if (cData?.success && cData.state) {
                            const comp = cData.state.companion || {};
                            enrichedMarketContext.companion_context = {
                                message_ar: comp.message_ar,
                                message_en: comp.message_en,
                                highlights: comp.highlights,
                                suggested_calls: comp.suggested_calls,
                                active_calls: cData.state.active_calls,
                                kind: comp.kind,
                            };
                        }
                    }
                } catch {
                    /* companion state optional */
                }
                try {
                    const reportRes = await elitePulseFetch(
                        "/api/ois/report-view",
                    );
                    if (reportRes.ok) {
                        const text = await reportRes.text();
                        let reportData: {
                            success?: boolean;
                            report?: unknown;
                        } = {};
                        try {
                            reportData = text?.trim() ? JSON.parse(text) : {};
                        } catch {
                            reportData = {};
                        }
                        if (reportData.success && reportData.report) {
                            enrichedMarketContext.report = reportData.report;
                        }
                    }
                } catch {
                    /* report optional */
                }
            }

            if (discussOnlyReport) {
                try {
                    const reportRes = await elitePulseFetch(
                        "/api/ois/report-view",
                    );
                    if (!reportRes.ok) {
                        enrichedMarketContext.discuss_only_report = true;
                        enrichedMarketContext.report = null;
                        enrichedMarketContext.report_error =
                            "Report not available";
                    } else {
                        const text = await reportRes.text();
                        let reportData: {
                            success?: boolean;
                            report?: unknown;
                            error?: string;
                        } = {};
                        try {
                            reportData =
                                text && text.trim() ? JSON.parse(text) : {};
                        } catch {
                            reportData = {};
                        }
                        if (reportData.success && reportData.report) {
                            enrichedMarketContext = {
                                ...marketContext,
                                discuss_only_report: true,
                                report: reportData.report,
                            };
                        } else {
                            enrichedMarketContext.discuss_only_report = true;
                            enrichedMarketContext.report = null;
                            enrichedMarketContext.report_error =
                                reportData.error || "Report not available";
                        }
                    }
                } catch (e) {
                    enrichedMarketContext.discuss_only_report = true;
                    enrichedMarketContext.report = null;
                    enrichedMarketContext.report_error =
                        e instanceof Error
                            ? e.message
                            : "Failed to load report";
                }
            }

            const tradingCallsSummary =
                chatMode === "strategy_trading_calls"
                    ? {
                          active: activeCalls.slice(0, 20).map((c) => ({
                              id: c.id,
                              symbol: c.symbol,
                              strategy: c.strategy?.type,
                              direction: c.direction,
                              entryPrice: c.entryPrice,
                              targetPrice: c.targetPrice,
                              stopLoss: c.stopLoss,
                              status: c.status,
                              confidence: c.confidence,
                          })),
                          closed: closedCalls.slice(0, 30).map((c) => ({
                              id: c.id,
                              symbol: c.symbol,
                              strategy: c.strategy?.type,
                              status: c.status,
                              outcome: c.performance?.outcome,
                              pnl: c.performance?.pnl,
                              pnlPercentage: c.performance?.pnlPercentage,
                          })),
                      }
                    : undefined;

            const flowAlertSettings = loadFlowAlertSettings();
            const authToken = token || getAuthToken();
            if (!authToken) {
                forceSessionExpired();
                throw new Error("HTTP 401: session expired");
            }

            const chatAbort = new AbortController();
            const chatTimeoutId = window.setTimeout(
                () => chatAbort.abort(),
                300_000,
            );

            let response: Response;
            try {
                response = await fetch(getApiUrl("/api/ai/chat"), {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        "Content-Type": "application/json",
                    },
                    signal: chatAbort.signal,
                    body: JSON.stringify({
                        message: currentInput,
                        conversation_history: conversationHistory,
                        market_context: buildChatPostMarketContext(
                            {
                                ...enrichedMarketContext,
                                chat_mode: chatMode,
                                trading_calls_summary: tradingCallsSummary,
                                flow_alert_settings: flowAlertSettings,
                            },
                            currentInput,
                        ),
                        language: language,
                        provider: provider,
                        discuss_only_report: discussOnlyReport,
                        chat_mode: chatMode,
                        trading_calls_summary: tradingCallsSummary,
                    }),
                });
            } finally {
                window.clearTimeout(chatTimeoutId);
            }

            if (!response.ok) {
                const errorText = await response.text();
                console.error(
                    "AI Chat Error - HTTP Status:",
                    response.status,
                    "Response:",
                    errorText,
                );
                if (response.status === 401 || response.status === 403) {
                    forceSessionExpired();
                }
                let detail = errorText;
                try {
                    const parsed = JSON.parse(errorText);
                    if (parsed?.error) detail = String(parsed.error);
                } catch {
                    /* plain text body */
                }
                throw new Error(
                    response.status === 401 || response.status === 403
                        ? `HTTP 401: ${detail || "session expired"}`
                        : `HTTP ${response.status}: ${response.statusText || detail}`,
                );
            }

            const data = await response.json();
            console.log("AI Chat Response:", {
                success: data.success,
                hasMessage: !!data.message,
                keys: Object.keys(data),
            });

            // Check if response is successful - handle both explicit success flag and presence of message
            if (data.success !== false && data.message) {
                let displayText = data.message;
                const walletAdded = data.paper_wallet_added as
                    | {
                          symbol?: string;
                          opened?: boolean;
                          already_open?: boolean;
                      }
                    | undefined;
                if (walletAdded?.symbol) {
                    window.dispatchEvent(
                        new CustomEvent("mmec-companion-wallet-add"),
                    );
                }
                let alertPatch = data.alert_config as
                    | Record<string, unknown>
                    | undefined;

                const extracted = extractAlertConfigFromMessage(displayText);
                displayText = extracted.cleanMessage || displayText;
                if (extracted.patch) {
                    alertPatch = { ...alertPatch, ...extracted.patch };
                }

                if (alertPatch && Object.keys(alertPatch).length > 0) {
                    const applied = await persistAlertPatch(token, alertPatch);
                    const summary = summarizeFlowAlertSettings(
                        applied,
                        language === "ar" ? "ar" : "en",
                    );
                    const confirmEn = `Alerts saved to your profile: ${summary}`;
                    const confirmAr = `تم حفظ التنبيهات في ملفك: ${summary}`;
                    let confirmLine = language === "ar" ? confirmAr : confirmEn;
                    const preview = data.alert_preview as
                        | AlertPreviewResult
                        | undefined;
                    if (preview?.sample_count && preview.samples?.length) {
                        const sample = preview.samples[0];
                        const prem = sample.premium ?? sample.Premium;
                        const tk = sample.ticker ?? sample.Ticker;
                        const extra =
                            language === "ar"
                                ? `معاينة QuestDB: ${preview.sample_count} صف (مثال ${tk} premium ${prem})`
                                : `QuestDB preview: ${preview.sample_count} match(es) (e.g. ${tk} premium ${prem})`;
                        confirmLine = `${confirmLine}\n${extra}`;
                    }
                    displayText = displayText
                        ? `${displayText.trim()}\n\n✅ ${confirmLine}`
                        : `✅ ${confirmLine}`;
                } else if (data.alert_profile) {
                    await syncAlertProfileFromServer(token);
                }

                const aiMessage: Message = {
                    id: (Date.now() + 1).toString(),
                    type: "ai",
                    content: sanitizeAiMessage(displayText),
                    timestamp: new Date(data.timestamp || Date.now()),
                    language: data.language,
                };
                setMessages((prev) => [...prev, aiMessage]);
                setError(null); // Clear any previous errors
            } else {
                // Use error code from backend if available, otherwise encrypt locally
                let errorDisplay: string;
                if (data.error_code && data.user_message) {
                    // Backend already encrypted the error - use it directly
                    errorDisplay = `${data.error_code}: ${data.user_message}`;
                    console.error(
                        "AI Chat Error - Backend error code:",
                        data.error_code,
                    );
                } else {
                    // Encrypt error locally - show code instead of actual error
                    const rawError =
                        data.error ||
                        (data.message
                            ? `Response: ${data.message.substring(0, 100)}`
                            : "Failed to get AI response");
                    console.error(
                        "AI Chat Error - Response validation failed:",
                        { data, rawError },
                    );
                    const errorInfo = encryptError(rawError);
                    errorDisplay = `${errorInfo.error_code}: ${errorInfo.user_message}`;
                }

                setError(errorDisplay);

                const errorMessage: Message = {
                    id: (Date.now() + 1).toString(),
                    type: "system",
                    content: errorDisplay,
                    timestamp: new Date(),
                };
                setMessages((prev) => [...prev, errorMessage]);
            }
        } catch (err: any) {
            const rawError =
                err?.name === "AbortError"
                    ? "Request timeout error"
                    : err.message || "Failed to send message";
            console.error("AI Chat Error - Exception:", rawError);

            // Encrypt error locally - show code instead of actual error
            const errorInfo = encryptError(rawError);
            const errorDisplay = `${errorInfo.error_code}: ${errorInfo.user_message}`;
            setError(errorDisplay);

            const errorMessage: Message = {
                id: (Date.now() + 1).toString(),
                type: "system",
                content: errorDisplay,
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const clearChat = () => {
        setMessages([]);
        setError(null);
    };

    // Palette tokens rather than a light/dark pair: the widget floats outside the shell canvas,
    // so the gray-class skin does not reach it.
    const bgClass = "bg-[color:var(--elev)]";
    const textClass = "text-[color:var(--txt)]";
    const borderClass = "border-[color:var(--hair)]";
    const inputBgClass = "bg-[color:var(--base)]";
    const buttonClass = "bg-[color:var(--acc)] hover:brightness-110 text-[color:var(--void)]";
    /** Secondary chip/button surface. */
    const chipClass = "bg-[color:var(--elev2)] hover:brightness-110";

    if (effectiveCollapsed && !embedded) {
        return (
            <button
                type="button"
                onClick={handleToggle}
                className={`fixed bottom-6 right-6 rounded-full shadow-lg cursor-pointer hover:shadow-2xl hover:scale-105 transition-all duration-200 z-40 border`}
                style={{
                    padding: "18px",
                    width: "64px",
                    height: "64px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: colors.primary,
                    color: colors.textInverse,
                    borderColor: colors.surfaceBorder,
                }}
                title={
                    language === "ar"
                        ? "فتح مساعد الذكاء الاصطناعي"
                        : "Open AI Assistant"
                }
                aria-expanded={false}
                aria-label={
                    language === "ar"
                        ? "فتح مساعد الذكاء الاصطناعي"
                        : "Open AI Assistant"
                }
            >
                <svg
                    className="w-8 h-8"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                </svg>
            </button>
        );
    }

    if (effectiveCollapsed && embedded) {
        return null;
    }

    return (
        <div
            className={`${embedded ? "relative z-0" : "fixed z-40"} ${bgClass} ${borderClass} border rounded-xl shadow-2xl flex flex-col transform transition-all`}
            style={
                embedded
                    ? {
                          width: "100%",
                          height: "100%",
                          minHeight: 0,
                          flex: 1,
                          borderRadius: 0,
                          boxShadow: "none",
                          border: "none",
                      }
                    : {
                          bottom: "24px",
                          right: "24px",
                          width: "min(420px, calc(100vw - 48px))",
                          height: "min(600px, calc(100vh - 100px))",
                          minWidth: "320px",
                          minHeight: "400px",
                          maxWidth: "90vw",
                          maxHeight: "85vh",
                      }
            }
        >
            {/* Header — compact inside MMEC Companion shell */}
            <div
                className={`${isCompanionEmbed ? "p-2" : "p-3"} ${borderClass} border-b flex flex-col gap-2 flex-shrink-0`}
            >
                {!isCompanionEmbed ? (
                    <div className="flex items-center justify-between gap-2 min-w-0">
                        <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
                            <svg
                                className="w-5 h-5 text-[color:var(--acc)] flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                                />
                            </svg>
                            <h3
                                className={`text-sm font-semibold ${textClass} truncate`}
                            >
                                {language === "ar"
                                    ? "مساعد الذكاء الاصطناعي"
                                    : "AI Assistant"}
                            </h3>
                            <span
                                className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                                    providerHealthOk === null
                                        ? "bg-[color:var(--muted)] animate-pulse"
                                        : providerHealthOk
                                          ? "bg-[color:var(--up)]"
                                          : "bg-[color:var(--down)]"
                                }`}
                                title={
                                    providerHealthOk === null
                                        ? language === "ar"
                                            ? "جاري التحقق من الاتصال…"
                                            : "Checking AI connection…"
                                        : providerHealthOk
                                          ? language === "ar"
                                              ? "متصل"
                                              : "Connected"
                                          : language === "ar"
                                            ? "غير متصل — سيتم إعادة المحاولة"
                                            : "Disconnected — retrying"
                                }
                            />
                        </div>
                        <button
                            type="button"
                            onClick={handleToggle}
                            className={`flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded ${chipClass} ${textClass}`}
                            title={
                                language === "ar"
                                    ? "إخفاء اللوحة"
                                    : "Hide panel"
                            }
                            aria-expanded
                        >
                            {language === "ar" ? "إخفاء" : "Hide"}
                        </button>
                    </div>
                ) : null}
                <div className="flex flex-wrap items-center gap-1.5">
                    <button
                        onClick={() => setShowWatchlist(!showWatchlist)}
                        className={`text-xs px-2 py-1 rounded ${chipClass} ${textClass} relative`}
                        title={
                            language === "ar" ? "قائمة المراقبة" : "Watchlist"
                        }
                    >
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                        </svg>
                        {watchlist.length > 0 && (
                            <span className="absolute -top-1 -right-1 bg-[color:var(--acc)] text-[color:var(--void)] text-[10px] font-semibold rounded-full w-5 h-5 flex items-center justify-center">
                                {watchlist.length}
                            </span>
                        )}
                    </button>
                    <select
                        value={chatMode}
                        onChange={(e) =>
                            setChatMode(e.target.value as ChatMode)
                        }
                        className={`text-xs px-2 py-1 rounded ${inputBgClass} ${textClass} border ${borderClass}`}
                        title={language === "ar" ? "وضع المحادثة" : "Chat mode"}
                    >
                        <option value="general">
                            {language === "ar" ? "عام" : "General"}
                        </option>
                        <option value="option_helper">
                            {language === "ar"
                                ? "مساعد الخيارات"
                                : "Option helper"}
                        </option>
                        <option value="strategy_trading_calls">
                            {language === "ar"
                                ? "استراتيجية وفرص"
                                : "Strategy & calls"}
                        </option>
                    </select>
                    {(chatMode === "option_helper" ||
                        chatMode === "strategy_trading_calls") && (
                        <button
                            onClick={() =>
                                setShowStrategyTable(!showStrategyTable)
                            }
                            className={`text-xs px-2 py-1 rounded ${showStrategyTable ? "bg-[color:var(--acc-dim)]" : chipClass} ${textClass}`}
                            title={
                                language === "ar"
                                    ? "جدول التوصيات والنتائج"
                                    : "Recommendations & outcomes table"
                            }
                        >
                            {language === "ar" ? "التتبع" : "Tracking"}
                        </button>
                    )}
                    <button
                        onClick={clearChat}
                        className={`text-xs px-2 py-1 rounded ${chipClass} ${textClass}`}
                    >
                        {language === "ar" ? "مسح" : "Clear"}
                    </button>
                </div>
            </div>

            {/* Messages */}
            <div
                ref={chatContainerRef}
                className={`flex-1 min-h-0 overflow-y-auto ${isCompanionEmbed ? "p-3" : "p-4"} space-y-4`}
            >
                {messages.length === 0 && (
                    <div className={`text-center ${textClass} opacity-60 mt-8`}>
                        <p>
                            {language === "ar"
                                ? "ابدأ محادثة مع مساعد الذكاء الاصطناعي"
                                : "Start a conversation with the AI assistant"}
                        </p>
                        <p className="text-sm mt-2">
                            {language === "ar"
                                ? "اسأل عن بيانات الخيارات، التحليل، أو الرؤى"
                                : "Ask about options data, analysis, or insights"}
                        </p>
                    </div>
                )}
                {messages.map((message) => (
                    <div
                        key={message.id}
                        className={`flex ${message.type === "user" ? "justify-end" : "justify-start"}`}
                    >
                        <div
                            className={`max-w-[80%] rounded-lg p-3 ${
                                message.type === "user"
                                    ? "bg-[color:var(--acc)] text-[color:var(--void)]"
                                    : message.type === "system"
                                      ? "bg-[color:var(--down-dim)] text-[color:var(--down)]"
                                      : `bg-[color:var(--elev2)] ${textClass}`
                            }`}
                        >
                            <p className="text-sm whitespace-pre-wrap">
                                {message.type === "ai"
                                    ? sanitizeAiMessage(message.content)
                                    : message.content}
                            </p>
                            {message.type !== "system" && (
                                <p className="text-xs opacity-60 mt-1">
                                    {new Date(
                                        message.timestamp,
                                    ).toLocaleTimeString()}
                                </p>
                            )}
                            {(chatMode === "option_helper" ||
                                chatMode === "strategy_trading_calls") &&
                                message.type === "ai" &&
                                message.content && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            handleTrackRecommendation(
                                                message.content,
                                            )
                                        }
                                        className={`mt-2 text-xs px-2 py-1 rounded ${chipClass} ${textClass}`}
                                    >
                                        {language === "ar"
                                            ? "تتبع هذه التوصية"
                                            : "Track this recommendation"}
                                    </button>
                                )}
                        </div>
                    </div>
                ))}
                {isLoading && (
                    <div className="flex justify-start">
                        <div
                            className={`bg-[color:var(--elev2)] ${textClass} rounded-lg p-3`}
                        >
                            <div className="flex gap-1">
                                <div
                                    className="w-2 h-2 bg-[color:var(--muted)] rounded-full animate-bounce"
                                    style={{ animationDelay: "0ms" }}
                                ></div>
                                <div
                                    className="w-2 h-2 bg-[color:var(--muted)] rounded-full animate-bounce"
                                    style={{ animationDelay: "150ms" }}
                                ></div>
                                <div
                                    className="w-2 h-2 bg-[color:var(--muted)] rounded-full animate-bounce"
                                    style={{ animationDelay: "300ms" }}
                                ></div>
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
                {!isCompanionEmbed &&
                    (chatMode === "option_helper" ||
                        chatMode === "strategy_trading_calls") && (
                        <div
                            className="mt-4 p-3 rounded-lg text-xs border"
                            style={{ background: "var(--warn-dim)", borderColor: "color-mix(in srgb, var(--warn) 40%, transparent)", color: "var(--warn)" }}
                        >
                            <p className="font-medium mb-1">
                                {language === "ar"
                                    ? "إخلاء المسؤولية"
                                    : "Disclaimer"}
                            </p>
                            <p className="opacity-95">
                                {language === "ar"
                                    ? "أي محتوى يتعلق بالتداول هو مجرد قراءة للبيانات وليس فرصة تداول فعلية؛ إنه تحسين لبناء العقود بناءً على المشاعر والمعلومات التي قدمتها. التداول على مسؤوليتك الخاصة فقط."
                                    : "Any trading-related content is only a reading of the data and is not an actual trading call; it is contract-building optimization based on sentiment and information you provided. Trade at your own risk."}
                            </p>
                        </div>
                    )}
            </div>

            {/* MMEC Preset Questions — Structured Intelligence Agent */}
            <div
                className={`${borderClass} border-t flex-shrink-0 ${isCompanionEmbed ? "max-h-32 overflow-hidden" : ""}`}
            >
                <button
                    type="button"
                    onClick={() => setPresetSectionOpen(!presetSectionOpen)}
                    className={`w-full px-4 py-2 flex items-center justify-between text-left hover:bg-[color:var(--elev2)] ${textClass} text-sm font-medium`}
                >
                    <span>
                        {language === "ar"
                            ? "وكيل MMEC الذكي — أسئلة جاهزة"
                            : "MMEC AI Terminal — Preset questions"}
                    </span>
                    <span className="text-xs opacity-70">
                        {presetSectionOpen ? "▼" : "▶"}
                    </span>
                </button>
                {presetSectionOpen && (
                    <div
                        className="px-4 pb-3 pt-1 max-h-48 overflow-y-auto bg-[color:var(--base)]"
                    >
                        <div className="mb-2">
                            <p
                                className={`text-xs font-medium mb-1.5 ${textClass} opacity-90`}
                            >
                                {language === "ar"
                                    ? "نظرة السوق"
                                    : "Market View"}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {MMEC_PRESET_MARKET_VIEW.map((preset) => (
                                    <button
                                        key={preset.id}
                                        type="button"
                                        onClick={() => {
                                            setInputMessage(
                                                getPresetQuestionText(
                                                    preset,
                                                    language === "ar"
                                                        ? "ar"
                                                        : "en",
                                                ),
                                            );
                                            setDiscussOnlyReport(true);
                                        }}
                                        className={`text-xs px-2 py-1.5 rounded border ${borderClass} ${chipClass} ${textClass}`}
                                        title={
                                            language === "ar"
                                                ? preset.en
                                                : preset.ar
                                        }
                                    >
                                        {language === "ar"
                                            ? preset.ar
                                            : preset.en}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-1.5">
                                <p
                                    className={`text-xs font-medium ${textClass} opacity-90`}
                                >
                                    {language === "ar"
                                        ? "تحليل سهم"
                                        : "Stock Analysis"}
                                </p>
                                <input
                                    type="text"
                                    value={presetSymbol}
                                    onChange={(e) =>
                                        setPresetSymbol(
                                            e.target.value.toUpperCase(),
                                        )
                                    }
                                    placeholder={
                                        language === "ar"
                                            ? "رمز السهم"
                                            : "Symbol"
                                    }
                                    className={`text-xs w-16 px-2 py-1 rounded border ${borderClass} ${inputBgClass} ${textClass}`}
                                />
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {MMEC_PRESET_STOCK_ANALYSIS.map((preset) => (
                                    <button
                                        key={preset.id}
                                        type="button"
                                        onClick={() => {
                                            setInputMessage(
                                                getPresetQuestionText(
                                                    preset,
                                                    language === "ar"
                                                        ? "ar"
                                                        : "en",
                                                    presetSymbol || undefined,
                                                ),
                                            );
                                            setDiscussOnlyReport(true);
                                        }}
                                        className={`text-xs px-2 py-1.5 rounded border ${borderClass} ${chipClass} ${textClass}`}
                                        title={
                                            language === "ar"
                                                ? preset.en
                                                : preset.ar
                                        }
                                    >
                                        {language === "ar"
                                            ? preset.ar
                                            : preset.en}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {(chatMode === "option_helper" ||
                            chatMode === "strategy_trading_calls") && (
                            <div className={`mt-2 pt-2 border-t ${borderClass}`}>
                                <p
                                    className={`text-xs font-medium mb-1.5 ${textClass} opacity-90`}
                                >
                                    {language === "ar"
                                        ? "استراتيجية خيارات"
                                        : "Options strategy"}
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                    {MMEC_PRESET_STRATEGY.map((preset) => (
                                        <button
                                            key={preset.id}
                                            type="button"
                                            onClick={() =>
                                                setInputMessage(
                                                    getPresetQuestionText(
                                                        preset,
                                                        language === "ar"
                                                            ? "ar"
                                                            : "en",
                                                        presetSymbol ||
                                                            undefined,
                                                    ),
                                                )
                                            }
                                            className={`text-xs px-2 py-1.5 rounded border ${borderClass} ${chipClass} ${textClass}`}
                                            title={
                                                language === "ar"
                                                    ? preset.en
                                                    : preset.ar
                                            }
                                        >
                                            {language === "ar"
                                                ? preset.ar
                                                : preset.en}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className={`mt-2 pt-2 border-t ${borderClass}`}>
                            <div className="flex items-center gap-2 mb-1.5">
                                <p
                                    className={`text-xs font-medium ${textClass} opacity-90`}
                                >
                                    {language === "ar"
                                        ? "تنبيهات التدفق والسيولة"
                                        : "Flow & liquidity alerts"}
                                </p>
                                <input
                                    type="text"
                                    value={presetSymbol}
                                    onChange={(e) =>
                                        setPresetSymbol(
                                            e.target.value.toUpperCase(),
                                        )
                                    }
                                    placeholder={
                                        language === "ar"
                                            ? "رمز (SPX)"
                                            : "Symbol (SPX)"
                                    }
                                    className={`text-xs w-20 px-2 py-1 rounded border ${borderClass} ${inputBgClass} ${textClass}`}
                                />
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {MMEC_PRESET_ALERTS.map((preset) => (
                                    <button
                                        key={preset.id}
                                        type="button"
                                        onClick={() => {
                                            if (preset.showAlertSettings) {
                                                void replyWithAlertSettings(
                                                    getPresetQuestionText(
                                                        preset,
                                                        language === "ar"
                                                            ? "ar"
                                                            : "en",
                                                    ),
                                                );
                                                return;
                                            }
                                            setInputMessage(
                                                getPresetQuestionText(
                                                    preset,
                                                    language === "ar"
                                                        ? "ar"
                                                        : "en",
                                                    presetSymbol || undefined,
                                                ),
                                            );
                                            setDiscussOnlyReport(false);
                                        }}
                                        className={`text-xs px-2 py-1.5 rounded border ${borderClass} ${
                                            preset.showAlertSettings
                                                ? "bg-[color:var(--warn-dim)] hover:brightness-110 border-[color:var(--warn)]"
                                                : chipClass
                                        } ${textClass}`}
                                        title={
                                            language === "ar"
                                                ? preset.en
                                                : preset.ar
                                        }
                                    >
                                        {language === "ar"
                                            ? preset.ar
                                            : preset.en}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Strategy recommendations table: track calls and outcomes for AI training/optimization */}
            {showStrategyTable &&
                (chatMode === "option_helper" ||
                    chatMode === "strategy_trading_calls") && (
                    <div
                        className={`px-4 py-2 ${borderClass} border-t max-h-48 overflow-auto`}
                    >
                        <p
                            className={`text-xs font-medium mb-2 ${textClass} opacity-90`}
                        >
                            {language === "ar"
                                ? "التوصيات والنتائج (لتحسين النموذج)"
                                : "Recommendations & outcomes (trains the model)"}
                        </p>
                        <table className="w-full text-xs">
                            <thead>
                                <tr
                                    className="text-[color:var(--muted)] text-[11px] tracking-wide"
                                >
                                    <th className="text-start py-1.5 font-semibold">Symbol</th>
                                    <th className="text-start py-1.5 font-semibold">
                                        {language === "ar"
                                            ? "استراتيجية"
                                            : "Strategy"}
                                    </th>
                                    <th className="text-start py-1.5 font-semibold">
                                        {language === "ar"
                                            ? "نتيجة"
                                            : "Outcome"}
                                    </th>
                                    <th className="text-end py-1.5 font-semibold">PnL</th>
                                    <th className="text-start py-1.5 font-semibold">
                                        {language === "ar" ? "إجراء" : "Action"}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className={textClass}>
                                {strategyRecommendations
                                    .slice(0, 15)
                                    .map((r) => (
                                        <tr
                                            key={r.id}
                                            className={`${borderClass} border-b`}
                                        >
                                            <td className="py-1.5">
                                                {r.symbol || "—"}
                                            </td>
                                            <td className="py-1.5">
                                                {r.strategy_type || "—"}
                                            </td>
                                            <td className="py-1.5">
                                                {r.outcome ||
                                                    (language === "ar"
                                                        ? "مفتوح"
                                                        : "open")}
                                            </td>
                                            <td className="py-1.5 text-end font-mono tabular-nums">
                                                {r.pnl != null ? r.pnl : "—"}
                                            </td>
                                            <td className="py-1.5">
                                                {!r.outcome ? (
                                                    <select
                                                        className={`text-xs ${inputBgClass} ${textClass} border ${borderClass} rounded px-1`}
                                                        onChange={(e) => {
                                                            const v =
                                                                e.target.value;
                                                            if (v)
                                                                recordOutcome(
                                                                    r.id,
                                                                    v,
                                                                );
                                                        }}
                                                        value=""
                                                    >
                                                        <option value="">
                                                            {language === "ar"
                                                                ? "تسجيل نتيجة"
                                                                : "Record outcome"}
                                                        </option>
                                                        <option value="target_hit">
                                                            {language === "ar"
                                                                ? "هدف"
                                                                : "Target hit"}
                                                        </option>
                                                        <option value="stop_loss">
                                                            {language === "ar"
                                                                ? "وقف"
                                                                : "Stop loss"}
                                                        </option>
                                                        <option value="expired">
                                                            {language === "ar"
                                                                ? "انتهى"
                                                                : "Expired"}
                                                        </option>
                                                        <option value="manual_close">
                                                            {language === "ar"
                                                                ? "إغلاق"
                                                                : "Manual close"}
                                                        </option>
                                                        <option value="cancelled">
                                                            {language === "ar"
                                                                ? "ملغى"
                                                                : "Cancelled"}
                                                        </option>
                                                    </select>
                                                ) : null}
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                        {strategyRecommendations.length === 0 && (
                            <p className={`text-xs ${textClass} opacity-70`}>
                                {language === "ar"
                                    ? "لا توجد توصيات مسجلة بعد."
                                    : 'No recommendations tracked yet. Use "Track this recommendation" on an AI reply.'}
                            </p>
                        )}
                    </div>
                )}

            {/* Strategy quick-answers: when in Option helper or Strategy & calls, help user answer AI questions in one tap */}
            {!isCompanionEmbed &&
                (chatMode === "option_helper" ||
                    chatMode === "strategy_trading_calls") && (
                    <div className={`px-4 py-2 ${borderClass} border-t`}>
                        <p
                            className={`text-xs font-medium mb-1.5 ${textClass} opacity-80`}
                        >
                            {language === "ar"
                                ? "إجابات سريعة لأسئلة الاستراتيجية"
                                : "Quick answers for strategy questions"}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                            {[
                                language === "ar"
                                    ? "أريد بيع علاوة (ائتمان)"
                                    : "I want to sell premium / collect theta",
                                language === "ar"
                                    ? "أريد التداول مع المشاعر (اتجاه)"
                                    : "I want to trade with sentiment (directional)",
                                language === "ar"
                                    ? "مع الأسهم (مثل مغطى)"
                                    : "With stock (e.g. covered call)",
                                language === "ar" ? "عارية فقط" : "Naked only",
                                language === "ar"
                                    ? "سبريد (عمودي/تقويم)"
                                    : "Spreads (vertical / calendar)",
                                language === "ar"
                                    ? "ياقات أو فراشات"
                                    : "Collars or butterflies",
                                language === "ar"
                                    ? "دين (أدفع علاوة)"
                                    : "Debit (I pay premium)",
                                language === "ar"
                                    ? "ائتمان (أستلم علاوة)"
                                    : "Credit (I receive premium)",
                                language === "ar"
                                    ? "اقترح أنت حسب البيانات"
                                    : "Suggest based on the data",
                            ].map((label) => (
                                <button
                                    key={label}
                                    type="button"
                                    onClick={() =>
                                        setInputMessage((prev) =>
                                            prev ? `${prev} ${label}` : label,
                                        )
                                    }
                                    className={`text-xs px-2 py-1 rounded border ${borderClass} ${chipClass} ${textClass}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

            {/* Input */}
            <div
                className={`${isCompanionEmbed ? "p-3" : "p-4"} ${borderClass} border-t flex-shrink-0`}
            >
                {!isCompanionEmbed ? (
                    <label
                        className={`flex items-center gap-2 mb-2 text-xs ${textClass} cursor-pointer`}
                    >
                        <input
                            type="checkbox"
                            checked={discussOnlyReport}
                            onChange={(e) =>
                                setDiscussOnlyReport(e.target.checked)
                            }
                            className="rounded accent-[color:var(--acc)]"
                        />
                        <span>
                            {language === "ar"
                                ? "مناقشة التقرير فقط (النبضة)"
                                : "Discuss only the report (Elite Pulse)"}
                        </span>
                    </label>
                ) : (
                    <p className={`mb-2 text-xs ${textClass} opacity-75`}>
                        {language === "ar"
                            ? "متصل بتقرير النبض والفرص التي يعرضها المرافق أعلاه."
                            : "Linked to the pulse brief and opportunities shown above."}
                    </p>
                )}
                {error && (
                    <div
                        className="mb-2 text-xs text-[color:var(--down)]"
                    >
                        {error}
                    </div>
                )}
                <div className="flex gap-2">
                    <textarea
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={
                            language === "ar"
                                ? "اكتب رسالتك..."
                                : "Type your message..."
                        }
                        className={`flex-1 ${inputBgClass} ${textClass} rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[color:var(--acc)]`}
                        rows={2}
                        disabled={isLoading}
                    />
                    <button
                        onClick={sendMessage}
                        disabled={isLoading || !inputMessage.trim()}
                        className={`px-4 py-2 rounded-lg ${buttonClass} disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                        {language === "ar" ? "إرسال" : "Send"}
                    </button>
                </div>
                <p
                    className="mt-1 text-xs text-[color:var(--muted)]"
                >
                    {language === "ar"
                        ? "للإجابة المختصرة: اكتب الزبدة أو Zubda"
                        : "For short answer (direction, targets, action): type Zubda or الزبدة"}
                </p>
            </div>

            {/* Track recommendation modal */}
            {trackModalOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={() => setTrackModalOpen(false)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className={`rounded-lg shadow-xl p-4 max-w-md w-full mx-2 ${bgClass} ${textClass} border ${borderClass}`}
                    >
                        <h4 className="font-semibold mb-3">
                            {language === "ar"
                                ? "تتبع التوصية"
                                : "Track this recommendation"}
                        </h4>
                        <label className="block text-xs mb-1">
                            {language === "ar" ? "الرمز" : "Symbol"}
                        </label>
                        <input
                            type="text"
                            value={trackPayload.symbol}
                            onChange={(e) =>
                                setTrackPayload((p) => ({
                                    ...p,
                                    symbol: e.target.value.toUpperCase(),
                                }))
                            }
                            placeholder="e.g. SPY"
                            className={`w-full mb-3 px-2 py-1.5 rounded border ${borderClass} ${inputBgClass} ${textClass} text-sm`}
                        />
                        <label className="block text-xs mb-1">
                            {language === "ar"
                                ? "نوع الاستراتيجية"
                                : "Strategy type"}
                        </label>
                        <input
                            type="text"
                            value={trackPayload.strategy_type}
                            onChange={(e) =>
                                setTrackPayload((p) => ({
                                    ...p,
                                    strategy_type: e.target.value,
                                }))
                            }
                            placeholder="e.g. Bull Put Spread, Covered Call"
                            className={`w-full mb-3 px-2 py-1.5 rounded border ${borderClass} ${inputBgClass} ${textClass} text-sm`}
                        />
                        <div className="flex gap-2 justify-end">
                            <button
                                type="button"
                                onClick={() => setTrackModalOpen(false)}
                                className={`px-3 py-1.5 rounded text-sm ${chipClass} ${textClass}`}
                            >
                                {language === "ar" ? "إلغاء" : "Cancel"}
                            </button>
                            <button
                                type="button"
                                onClick={submitTrackRecommendation}
                                disabled={
                                    !trackPayload.symbol.trim() ||
                                    !trackPayload.strategy_type.trim()
                                }
                                className={`px-3 py-1.5 rounded text-sm ${buttonClass} disabled:opacity-50`}
                            >
                                {language === "ar" ? "حفظ" : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Floating minimize — hidden inside companion shell (parent Close button) */}
            {!embedded ? (
                <button
                    type="button"
                    onClick={handleToggle}
                    className="absolute bottom-4 right-4 z-20 flex h-11 w-11 items-center justify-center rounded-full border shadow-md transition-transform hover:scale-105"
                    style={{
                        backgroundColor: colors.primary,
                        color: colors.textInverse,
                        borderColor: colors.surfaceBorder,
                    }}
                    title={
                        language === "ar"
                            ? "تصغير المساعد"
                            : "Minimize assistant"
                    }
                    aria-label={
                        language === "ar"
                            ? "تصغير المساعد"
                            : "Minimize assistant"
                    }
                >
                    <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                </button>
            ) : null}

            {/* Watchlist Manager Modal */}
            {showWatchlist && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={() => setShowWatchlist(false)}
                >
                    <div onClick={(e) => e.stopPropagation()}>
                        <WatchlistManager
                            data={marketContext?.rawData || []}
                            onClose={() => setShowWatchlist(false)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default AIChatWidget;
